import { useQuery } from '@tanstack/react-query'
import { api, buildQueryString } from '@/lib/api'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { FilterValues } from './SupplyChainFilters'

/**
 * Aggregate stats tile rail for `/threats/supply-chain`. Mirrors the shape
 * returned by `projectStatsForList()` (see
 * `apps/api/src/supply-chain/projectors/projectStatsForList.ts`):
 *   - `totals`           — total / last7d / last30d
 *   - `bySeverity`       — critical / high / medium / low / unknown
 *   - `bySecurityRelevance` — security_relevant / not_security_relevant
 *
 * The endpoint accepts the same filter axes as `/incidents` minus `page`,
 * so the rail reflects the user's current filter selection.
 */
export interface SupplyChainStatsResponse {
  totals: { total: number; last7d: number; last30d: number }
  bySeverity: {
    critical: number
    high: number
    medium: number
    low: number
    unknown: number
  }
  bySecurityRelevance: {
    security_relevant: number
    not_security_relevant: number
  }
}

function buildStatsParams(filters: FilterValues): Record<string, string> {
  const out: Record<string, string> = {}
  if (filters.provider) out.provider = filters.provider
  if (filters.severity) out.severity = filters.severity
  if (filters.security_relevant !== undefined) {
    out.security_relevant = String(filters.security_relevant)
  }
  if (filters.from) out.from = filters.from
  if (filters.to) out.to = filters.to
  return out
}

export function SupplyChainStats({ filters }: { filters: FilterValues }) {
  const params = buildStatsParams(filters)
  const qs = buildQueryString(params)
  const { data, isLoading } = useQuery<SupplyChainStatsResponse>({
    queryKey: ['supply-chain', 'stats', params],
    queryFn: () => api.get<SupplyChainStatsResponse>(`/supply-chain/stats${qs}`),
  })

  if (isLoading) {
    return (
      <div
        data-testid="supply-chain-stats-loading"
        className="grid gap-2 md:grid-cols-4"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  if (!data) return null

  const tiles = [
    {
      key: 'last7d',
      value: data.totals.last7d,
      label: 'last 7 days',
      tone: 'text-foreground',
    },
    {
      key: 'critical',
      value: data.bySeverity.critical,
      label: 'critical',
      tone: 'text-red-600 dark:text-red-400',
    },
    {
      key: 'high',
      value: data.bySeverity.high,
      label: 'high',
      tone: 'text-orange-600 dark:text-orange-400',
    },
    {
      key: 'security_relevant',
      value: data.bySecurityRelevance.security_relevant,
      label: 'security-relevant',
      tone: 'text-foreground',
    },
  ] as const

  return (
    <div
      data-testid="supply-chain-stats"
      className="grid gap-2 md:grid-cols-4"
    >
      {tiles.map((t) => (
        <Card key={t.key} className="p-4">
          <div className={`text-3xl font-semibold tabular-nums ${t.tone}`}>
            {t.value}
          </div>
          <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
            {t.label}
          </div>
        </Card>
      ))}
    </div>
  )
}
